<template>
  <div class="flex flex-col gap-6 proxima_nova text_color">
    <div class="flex flex-col gap-2">
      <h1 class="text-xl font-semibold">Unibox</h1>
      <h2 class="text-base font-semibold">Assign channels</h2>
      <p class="text-sm secondary_color">
        Select what conversations to show up for each channel in your unibox
      </p>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <CustomLoader />
    </div>

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-4">
        <h3 class="text-base font-semibold">WhatsApp</h3>
        
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium">Accounts to show in unibox</span>
          
          <a-select
            v-model:value="selectedPhoneNumbers"
            mode="multiple"
            placeholder="Select phone numbers"
            :options="phoneNumberOptions"
            :filter-option="filterOption"
            class="w-2/3"
            show-search
            allow-clear
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 mt-2">
        <div class="flex items-center gap-3">
          <a-switch
            size="small"
            v-model:checked="aiAgentEnabled"
            :loading="togglingAiAgent"
            @change="handleAiAgentToggle"
          />
          <span class="text-base font-semibold">AI Agent Responses</span>
        </div>
        <span class="text-sm secondary_color ml-10">
          You will see the messages in the Support Inbox. Make sure to setup the
          <span class="font-semibold">knowledge base</span> and
          <span class="font-semibold">agent instructions</span> from the Support page.
        </span>
      </div>

      <div class="flex justify-start">
        <a-button
          class="primary_medium"
          :loading="saving"
          @click="handleSave"
        >
          Save Changes
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { message } from "ant-design-vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import UniboxService from "@/src/api/UniboxService.js";
import ConversationService from "@/src/api/ConversationService.js";
import eventBus from "@/src/utils/eventBus.js";
import { normalizePhoneNumber } from "@/src/utils/phoneNumberUtils.js";

const loading = ref(false);
const saving = ref(false);
const togglingAiAgent = ref(false);
const aiAgentEnabled = ref(false);
const allPhoneNumbers = ref([]);
const selectedPhoneNumbers = ref([]);
const supportAssignedNumbers = ref([]);

const phoneNumberOptions = computed(() => {
  return allPhoneNumbers.value
    .filter((config) => !supportAssignedNumbers.value.includes(normalizePhoneNumber(config.phone_number)))
    .map((config) => ({
      label: config.phone_number,
      value: config.phone_number,
    }));
});

const filterOption = (input, option) => {
  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
};

const fetchAllPhoneNumbers = async () => {
  try {
    const response = await UniboxService.fetchWhatsAppConfigs(true);
    if (response && response.success && response.configs) {
      allPhoneNumbers.value = response.configs.map((config) => ({
        id: config.id,
        phone_number: normalizePhoneNumber(config.phone_number),
      }));
    }
  } catch (error) {
    console.error("Error fetching all phone numbers:", error);
    message.error("Failed to fetch phone numbers");
  }
};

const fetchSelectedPhoneNumbers = async () => {
  try {
    const response = await UniboxService.fetchUniboxWhatsAppNumbers();
    if (response && response.success && response.phone_numbers) {
      selectedPhoneNumbers.value = response.phone_numbers;
    }
  } catch (error) {
    console.error("Error fetching selected phone numbers:", error);
  }
};

const fetchSupportAssignedNumbers = async () => {
  try {
    const response = await ConversationService.getSupportWhatsAppSettings();
    if (response && response.success && response.data?.default_phone_number) {
      supportAssignedNumbers.value = [normalizePhoneNumber(response.data.default_phone_number)];
    }
  } catch (error) {
    console.error("Error fetching support WhatsApp settings:", error);
  }
};

const fetchAiAgentStatus = async () => {
  try {
    const response = await ConversationService.getAgentChannelSettings();
    if (response && response.success && response.data) {
      const whatsappSetting = response.data.find((s) => s.channel === "whatsapp");
      if (whatsappSetting) {
        aiAgentEnabled.value = whatsappSetting.bot_enabled;
      }
    }
  } catch (error) {
    console.error("Error fetching AI agent status:", error);
  }
};

const handleAiAgentToggle = async (checked) => {
  try {
    togglingAiAgent.value = true;
    const response = await ConversationService.toggleAgentChannel({
      channel: "whatsapp",
      bot_enabled: checked,
    });

    if (response && response.success) {
      message.success(checked ? "AI Agent enabled for WhatsApp" : "AI Agent disabled for WhatsApp");
    } else {
      message.error("Failed to update AI Agent setting");
      aiAgentEnabled.value = !checked;
    }
  } catch (error) {
    console.error("Error toggling AI agent:", error);
    message.error("Failed to update AI Agent setting");
    aiAgentEnabled.value = !checked;
  } finally {
    togglingAiAgent.value = false;
  }
};

const handleSave = async () => {
  try {
    saving.value = true;
    const response = await UniboxService.saveUniboxWhatsAppNumbers({
      phone_numbers: selectedPhoneNumbers.value,
    });
    
    if (response && response.success) {
      message.success("Settings saved successfully");
      eventBus.emit("unibox-channels-updated");
    } else {
      message.error("Failed to save settings");
    }
  } catch (error) {
    console.error("Error saving settings:", error);
    message.error("An error occurred while saving settings");
  } finally {
    saving.value = false;
  }
};

const init = async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchAllPhoneNumbers(),
      fetchSelectedPhoneNumbers(),
      fetchSupportAssignedNumbers(),
      fetchAiAgentStatus(),
    ]);
  } catch (error) {
    console.error("Error initializing Unibox settings:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  init();
});
</script>

